import { useState } from "react";

import { Form } from "react-bootstrap";

import {
  Card,
  CollapseToggle,
  GridForm,
  GridFormItemWrapper,
  IconButton,
} from "@bagel-web/components";

import { supportedComponent } from "./supportedComponents";
import ServiceParamValueField, {
  ServiceParamValue,
} from "../../service/ServiceParamValueField";
import { PlaygroundComponentConfig } from "../../types";
import { validateServiceParameter } from "../../util/services";

// The component id editor. A valid id applies on every keystroke through
// the page's rename cascade (seed channel, seed messages, and presentation
// entries follow the id); an empty or colliding id would merge or orphan
// state, so it is held locally and flagged instead, and blur reverts it.
const ComponentIdField = ({
  id,
  component,
  takenIds,
  onRename,
}: {
  id: string;
  component: PlaygroundComponentConfig;
  takenIds: Set<string>;
  onRename?: (component: PlaygroundComponentConfig, nextId: string) => void;
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const componentId = typeof component.id === "string" ? component.id : "";
  return (
    <Form.Control
      id={id}
      className="font-monospace"
      data-testid={`playground-component-id-${componentId}`}
      value={draft ?? componentId}
      isInvalid={draft !== null}
      onChange={(event) => {
        const next = event.target.value;
        if (next === componentId) {
          setDraft(null);
          return;
        }
        if (next === "" || takenIds.has(next)) {
          setDraft(next);
          return;
        }
        setDraft(null);
        onRename?.(component, next);
      }}
      onBlur={() => setDraft(null)}
    />
  );
};

// One configured component: its id and type, its type's configurable
// fields behind the Advanced toggle, and its delete control.
const ComponentConfigCard = ({
  index,
  component,
  takenIds,
  onFieldChange,
  onDelete,
  onRename,
}: {
  // Position in the config list; the field ids key off it because the
  // component id is itself editable.
  index: number;
  component: PlaygroundComponentConfig;
  // The other components' ids, which this one's may not collide with.
  takenIds: Set<string>;
  onFieldChange: (name: string, value: ServiceParamValue) => void;
  onDelete: () => void;
  onRename?: (component: PlaygroundComponentConfig, nextId: string) => void;
}) => {
  const fieldEntries = Object.entries(
    supportedComponent(component.type)?.fields ?? {}
  );
  return (
    <Card>
      {/* The trash shares the row with the fields instead of holding a
          toolbar band of its own. */}
      <div className="d-flex align-items-start gap-3">
        <div className="flex-grow-1">
          <GridForm $width="100%" $numCols={2}>
            <GridFormItemWrapper>
              <Form.Label htmlFor={`playground-component-id-${index}`}>
                Component ID
              </Form.Label>
              <ComponentIdField
                id={`playground-component-id-${index}`}
                component={component}
                takenIds={takenIds}
                onRename={onRename}
              />
            </GridFormItemWrapper>
            <GridFormItemWrapper>
              <Form.Label htmlFor={`playground-component-type-${index}`}>
                Type
              </Form.Label>
              <Form.Control
                id={`playground-component-type-${index}`}
                className="font-monospace"
                style={{ color: "var(--bs-code-color)" }}
                plaintext
                readOnly
                value={component.type ?? ""}
                data-testid={`playground-component-type-${component.id}`}
              />
            </GridFormItemWrapper>
          </GridForm>
          {fieldEntries.length > 0 && (
            <CollapseToggle
              id={`playground-component-advanced-${index}`}
              label="Advanced"
              className="mt-2"
            >
              <GridForm $width="100%" $numCols={2}>
                {fieldEntries.map(([name, schema]) => {
                  const raw = component[name];
                  const set =
                    typeof raw === "string" ||
                    typeof raw === "number" ||
                    typeof raw === "boolean"
                      ? raw
                      : undefined;
                  // An unset boolean has no placeholder to show its
                  // default through, so the default is the shown value.
                  const value =
                    set === undefined && schema.type === "boolean"
                      ? (schema.default as boolean | undefined)
                      : set;
                  const fieldId = `playground-component-${component.id}-${name}`;
                  return (
                    <GridFormItemWrapper key={name}>
                      <Form.Label htmlFor={fieldId}>
                        {typeof schema.title === "string" ? schema.title : name}
                      </Form.Label>
                      <ServiceParamValueField
                        id={fieldId}
                        name={fieldId}
                        schema={schema}
                        value={value}
                        placeholder={
                          schema.default !== undefined
                            ? String(schema.default)
                            : undefined
                        }
                        isInvalid={
                          set !== undefined &&
                          !validateServiceParameter(set, schema)
                        }
                        onChange={(next) => onFieldChange(name, next)}
                      />
                    </GridFormItemWrapper>
                  );
                })}
              </GridForm>
            </CollapseToggle>
          )}
        </div>
        <IconButton
          className="bi-trash-fill"
          variant="secondary"
          tooltip="Delete this component."
          data-testid={`playground-component-delete-${component.id}`}
          onClick={onDelete}
        />
      </div>
    </Card>
  );
};

export default ComponentConfigCard;
